/**
 * Per-stage controllers: the interactive middle of a memory stage, between the base MemoryStage's
 * intro timeline and the stage's closing timeline.
 *
 *   storm    — 20 s of keeping footing on the pitching deck (← →), sailors shouting, lightning.
 *   roam     — free walking + InteractionSystem over the StageDef interactions; a `main` trigger ends it.
 *   gaze     — hold confirm to stare at the black triangle until the black water comes.
 *   scripted — plays the closing timeline straight away (the road: the lesson already happened).
 *
 * Every controller converges on skip(): the closing timeline is fast-forwarded (state only) and
 * run() resolves 'skipped'. Phaser-free except through the StageContext callbacks.
 */
import type { MapInteraction, IActor } from '@/core/types/contracts';
import type { SeededRng } from '@/core/rng';
import type { CinematicResult } from '@/cinematics';
import { InteractionSystem, type InteractionGate } from '@/interaction';
import type { PrologueEffects } from '../effects/PrologueEffects';
import { GAZE_HOLD_S, STORM_CAPTIONS, STORM_DURATION_S, STORM_SAILOR_POSTS, STORM_SHOUTS, type StageDef, type StageInteraction } from './defs';

export interface StageContext {
  def: StageDef;
  rng: SeededRng;
  actors: Map<string, IActor>;
  playerActor: IActor;
  fx: PrologueEffects;
  gate: InteractionGate;
  /** Plays a registered timeline on the stage's cinematic player (fastForward = state only). */
  playTimeline(id: string, fastForward?: boolean): Promise<CinematicResult>;
  /** Runs a dialogue (resolves when it ends). */
  runDialogue(id: string): Promise<void>;
  /** Skips whatever is playing (cinematic or dialogue). Returns true when something was skipped. */
  skipPlaying(): boolean;
  /** True while a dialogue / cinematic / UI modal owns the input. */
  isBusy(): boolean;
  setPlayerControl(on: boolean): void;
  showHint(text: string, ms?: number): void;
  showCaption(text: string, ms: number): void;
  showSpeech(actorId: string, text: string, ms: number): void;
  setMeter(label: string | null, value?: number): void;
  setSway(lean: number): void;
  cameraShake(ms: number, intensity: number): void;
  sfx(key: string): void;
  confirmHeld(): boolean;
  dirX(): number;
  /** Timer that resolves early on scene shutdown. */
  wait(ms: number): Promise<void>;
  isAlive(): boolean;
}

export interface StageController {
  run(): Promise<CinematicResult>;
  step(dtMs: number): void;
  /** Cancel pressed while nothing scripted is playing: converge to the end state. */
  skip(): void;
  /** Confirm pressed while nothing scripted is playing. */
  confirm(): void;
  destroy(): void;
}

abstract class Base implements StageController {
  protected done = false;
  protected skipped = false;
  private resolveRun: ((r: CinematicResult) => void) | null = null;
  private runPromise: Promise<CinematicResult> | null = null;
  constructor(protected readonly ctx: StageContext) {}

  run(): Promise<CinematicResult> {
    if (this.runPromise) return this.runPromise;
    this.runPromise = new Promise<CinematicResult>((resolve) => { this.resolveRun = resolve; });
    if (this.skipped) this.finish('skipped'); else this.start();
    return this.runPromise;
  }
  protected abstract start(): void;
  step(_dtMs: number): void { /* optional */ }
  confirm(): void { /* optional */ }
  skip(): void {
    if (this.done) return;
    this.skipped = true;
    if (this.ctx.skipPlaying()) return; // the running timeline/dialogue converges on its own
    if (this.resolveRun) this.finish('skipped');
  }
  destroy(): void { this.done = true; }

  protected finish(result: CinematicResult): void {
    if (this.done) return;
    this.done = true;
    this.onFinish();
    const r = this.resolveRun; this.resolveRun = null;
    void this.closing(result).then((final) => r?.(final), (e) => { console.warn('[stage] closing failed', e); r?.(result); });
  }
  protected onFinish(): void { /* optional */ }
  /** Plays the closing timeline (fast when skipped). */
  protected async closing(result: CinematicResult): Promise<CinematicResult> {
    const main = this.ctx.def.main;
    if (!main) return result;
    const r = await this.ctx.playTimeline(main, result === 'skipped');
    return result === 'skipped' || r === 'skipped' ? 'skipped' : 'completed';
  }
}

// ------------------------------------------------------------------ storm
class StormController extends Base {
  private t = 0;
  private balance = 0;
  private captionIdx = 0;
  private nextShout = 2.2;
  private nextBolt = 3.5;
  private stumbles = 0;
  private readonly phase: [number, number];
  constructor(ctx: StageContext) {
    super(ctx);
    this.phase = [ctx.rng.range(0, Math.PI * 2), ctx.rng.range(0, Math.PI * 2)];
  }
  protected start(): void {
    const { ctx } = this;
    ctx.showHint(ctx.def.hint ?? '', 5000);
    ctx.setMeter('站稳', 0.5);
    ctx.playerActor.play('idle');
    for (const id of Object.keys(STORM_SAILOR_POSTS)) void this.sailorLoop(id);
  }
  private async sailorLoop(id: string): Promise<void> {
    const a = this.ctx.actors.get(id);
    const posts = STORM_SAILOR_POSTS[id] ?? [];
    if (!a || posts.length === 0) return;
    let i = 0;
    const live = (): boolean => !this.done && this.ctx.isAlive();
    while (live()) {
      const post = posts[i % posts.length] as [number, number];
      await a.walkTo(post[0] * 32 + 16, post[1] * 32 + 28, 92);
      if (!live()) break; // the stage ended (skip / shutdown) while walking: the actor may be destroyed
      a.play('emote');
      await this.ctx.wait(this.ctx.rng.range(500, 1300));
      if (!live()) break;
      a.play('idle');
      i++;
    }
  }
  private lean(): number { return Math.sin(this.t * 1.3 + this.phase[0]) * 0.7 + Math.sin(this.t * 2.9 + this.phase[1]) * 0.3; }
  override step(dtMs: number): void {
    if (this.done) return;
    const { ctx } = this;
    const dt = dtMs / 1000;
    this.t += dt;
    const lean = this.lean();
    ctx.setSway(lean);
    if (ctx.isBusy()) return;
    const input = ctx.dirX();
    this.balance = Math.max(-1.3, Math.min(1.3, this.balance + (lean * 0.9 - input * 1.5) * dt));
    const p = ctx.playerActor as IActor & { setVelocity?: (vx: number, vy: number) => void };
    if (p.setVelocity) { const vx = input * 46 + lean * 26; if (Math.abs(vx) > 0.5) p.setVelocity(vx, 0); }
    if (Math.abs(this.balance) >= 1) {
      this.balance = 0; this.stumbles++;
      void ctx.playerActor.showEmote('sweat', 700);
      ctx.cameraShake(220, 3);
      ctx.sfx('sfx-stumble');
    }
    ctx.setMeter('站稳', (this.balance + 1) / 2);
    const cap = STORM_CAPTIONS[this.captionIdx];
    if (cap && this.t >= cap.at) { this.captionIdx++; ctx.showCaption(cap.text, 6500); }
    if (this.t >= this.nextShout) {
      this.nextShout = this.t + ctx.rng.range(2.4, 4.2);
      const ids = Object.keys(STORM_SAILOR_POSTS);
      ctx.showSpeech(ctx.rng.pick(ids), ctx.rng.pick(STORM_SHOUTS), 1600);
    }
    if (this.t >= this.nextBolt) {
      this.nextBolt = this.t + ctx.rng.range(3, 6.5);
      void ctx.fx.play('lightning', { shake: 4 });
      ctx.sfx('sfx-thunder');
    }
    if (this.t >= STORM_DURATION_S) this.finish('completed');
  }
  protected override onFinish(): void {
    this.ctx.setMeter(null);
    this.ctx.setSway(0);
    try { this.ctx.playerActor.stop(); } catch { /* ignore */ }
  }
}

// ------------------------------------------------------------------ roam
function toMapInteraction(i: StageInteraction): MapInteraction {
  const m: MapInteraction = { id: i.id, x: i.x, y: i.y, w: i.w, h: i.h, kind: i.kind, label: i.label };
  if (i.dialogue) m.dialogue = i.dialogue;
  if (i.timeline) m.cinematic = i.timeline;
  if (i.auto) m.auto = true;
  return m;
}

class RoamController extends Base {
  private readonly system: InteractionSystem;
  private readonly fired = new Set<string>();
  private running = false;
  constructor(ctx: StageContext) {
    super(ctx);
    const interactions = ctx.def.interactions.map(toMapInteraction);
    this.system = new InteractionSystem({ interactions, exits: [] }, ctx.gate, {
      onTalk: (i) => void this.talk(i),
      onObserve: (i) => void this.observe(i),
      onTrigger: (i) => void this.trigger(i),
    });
    this.system.setLocked(true);
  }
  protected start(): void {
    this.ctx.showHint(this.ctx.def.hint ?? '', 6000);
    this.ctx.setPlayerControl(true);
    this.system.setLocked(false);
  }
  override step(_dtMs: number): void {
    if (this.done) return;
    const busy = this.ctx.isBusy() || this.running;
    this.system.setLocked(busy);
    if (busy) return;
    const p = this.ctx.playerActor;
    this.system.update(p.x, p.y, p.facing);
  }
  override confirm(): void {
    if (this.done || this.running || this.ctx.isBusy()) return;
    this.system.tryInteract();
  }
  private stageInteraction(id: string): StageInteraction | undefined { return this.ctx.def.interactions.find((x) => x.id === id); }
  private async talk(i: MapInteraction): Promise<void> {
    if (!i.dialogue || this.running) return;
    const def = this.stageInteraction(i.id);
    const other = def?.actor ? this.ctx.actors.get(def.actor) : undefined;
    const p = this.ctx.playerActor;
    if (other) { other.faceTowards(p.x, p.y); p.faceTowards(other.x, other.y); }
    await this.guardedDialogue(i.dialogue);
  }
  private async observe(i: MapInteraction): Promise<void> {
    if (!i.dialogue || this.running) return;
    await this.guardedDialogue(i.dialogue);
  }
  private async guardedDialogue(id: string): Promise<void> {
    this.running = true;
    this.ctx.setPlayerControl(false);
    try { await this.ctx.runDialogue(id); } finally { this.running = false; if (!this.done) this.ctx.setPlayerControl(true); }
  }
  private async trigger(i: MapInteraction): Promise<void> {
    const def = this.stageInteraction(i.id);
    if (!def?.timeline || this.running) return;
    if (def.main && this.fired.has(i.id)) return;
    this.fired.add(i.id);
    this.running = true;
    this.ctx.setPlayerControl(false);
    let result: CinematicResult = 'completed';
    try {
      result = await this.ctx.playTimeline(def.timeline);
      if (def.then && !this.done) { const r2 = await this.ctx.playTimeline(def.then); if (r2 === 'skipped') result = 'skipped'; }
    } finally { this.running = false; }
    if (def.main) this.finish(result);
    else if (!this.done) this.ctx.setPlayerControl(true);
  }
  override skip(): void {
    if (this.done) return;
    this.skipped = true;
    if (this.ctx.skipPlaying()) return;
    if (this.running) return; // a trigger is mid-flight; it will resolve
    void this.fastForwardMain();
  }
  private async fastForwardMain(): Promise<void> {
    const main = this.ctx.def.interactions.find((i) => i.main);
    this.running = true;
    this.ctx.setPlayerControl(false);
    try {
      if (main?.timeline) await this.ctx.playTimeline(main.timeline, true);
      if (main?.then) await this.ctx.playTimeline(main.then, true);
    } finally { this.running = false; }
    this.finish('skipped');
  }
  protected override onFinish(): void {
    this.system.destroy();
    this.ctx.setPlayerControl(false);
  }
  override destroy(): void { super.destroy(); this.system.destroy(); }
}

// ------------------------------------------------------------------ gaze
class GazeController extends Base {
  private progress = 0;
  private t = 0;
  private nothingShown = false;
  private touchPulse = 0;
  protected start(): void {
    this.ctx.showHint(this.ctx.def.hint ?? '', 6000);
    this.ctx.setMeter('盯着看', 0);
  }
  override confirm(): void { this.touchPulse = 0.7; }
  override step(dtMs: number): void {
    if (this.done) return;
    const dt = dtMs / 1000;
    this.t += dt;
    if (this.ctx.isBusy()) return;
    const held = this.ctx.confirmHeld() || this.touchPulse > 0;
    this.touchPulse = Math.max(0, this.touchPulse - dt);
    this.progress = Math.max(0, Math.min(GAZE_HOLD_S, this.progress + (held ? dt : -dt * 0.6)));
    const ratio = this.progress / GAZE_HOLD_S;
    this.ctx.setMeter('盯着看', ratio);
    const tri = this.ctx.fx.named.get('black-triangle') as { setAlpha?: (a: number) => unknown } | undefined;
    tri?.setAlpha?.(0.55 + 0.35 * ratio + Math.sin(this.t * (4 + ratio * 8)) * 0.1);
    if (held && ratio > 0.08 && ratio < 0.95 && Math.floor(this.t * 6) % 3 === 0) this.ctx.cameraShake(60, 1);
    if (!this.nothingShown && ratio >= 0.4) {
      this.nothingShown = true;
      this.progress = Math.max(this.progress, GAZE_HOLD_S * 0.45);
      void this.ctx.runDialogue('prologue-water-nothing');
      return;
    }
    if (this.progress >= GAZE_HOLD_S) this.finish('completed');
  }
  protected override onFinish(): void { this.ctx.setMeter(null); }
}

// ------------------------------------------------------------------ scripted (road)
class ScriptedController extends Base {
  protected start(): void { this.finish('completed'); }
}

export function createController(ctx: StageContext): StageController {
  switch (ctx.def.kind) {
    case 'storm': return new StormController(ctx);
    case 'roam': return new RoamController(ctx);
    case 'gaze': return new GazeController(ctx);
    case 'scripted': return new ScriptedController(ctx);
  }
}
